import type { BannerItem } from "$root/components/banner-stack/types";
import { customerDecimalPlaces, type IUser } from "$root/functional-areas/user";
import { ActionText, AutoTooltip } from "@mdotm/shadcn/legacy/components";
import type { TFunction } from "i18next";
import { Set } from "immutable";
import { type InstrumentEditorBuilderProps, normaliseAndVerifyInstrumentPortfolioComposition } from "../builder";
import type { Instrument, InstrumentEntities } from "../const";
import { weightTotalStatus } from "../weight-total";

export type NormaliseAndExcessToCashBannerDeps = {
	instrumentBuilder: InstrumentEditorBuilderProps;
	entity: InstrumentEntities;
	/** Normalise spreads the gap over the selected rows, so it needs at least one. */
	selection: Instrument[];
	/** Commits or drops the cell being edited, so the normalise reads settled weights. */
	stopEditing(): void;
	t: TFunction;
	/** Whose customer precision decides whether the total is 100. */
	user: Pick<IUser, "customerDecimalPlacesPreferences">;
};

export function normaliseAndExcessToCashBanner({
	instrumentBuilder,
	entity,
	selection,
	stopEditing,
	t,
	user,
}: NormaliseAndExcessToCashBannerDeps): BannerItem | null {
	const status = weightTotalStatus(instrumentBuilder.getTotal("weight"), customerDecimalPlaces(user));
	if (status === "exact") {
		return null;
	}
	const hasSelection = selection.length > 0;

	return {
		id: "normalise-and-excess-to-cash",
		severity: "warning",
		title:
			status === "under"
				? t("EDIT_COMPOSITION.NORMALIZE_BOX.LESS_THAN_100.TITLE")
				: t("EDIT_COMPOSITION.NORMALIZE_BOX.MORE_THAN_100.TITLE"),
		description: (
			<>
				<span>
					Select at least one instrument and click&nbsp;
					<AutoTooltip
						severity="warning"
						disabled={hasSelection}
						trigger={({ innerRef }) => (
							<ActionText
								className="cursor-pointer"
								innerRef={innerRef}
								disabled={!hasSelection}
								onClickAsync={async () => {
									stopEditing();
									await normaliseAndVerifyInstrumentPortfolioComposition({
										instrumentBuilder,
										entity,
										selection: Set<string>(selection.map((x) => x.id)),
									});
								}}
							>
								Normalise.
							</ActionText>
						)}
					>
						Select at least one instrument
					</AutoTooltip>
					&nbsp;
					{status === "under"
						? "Sphere will proportionally add weights to the selected instruments."
						: "Sphere will proportionally remove weights to the selected instruments."}
				</span>
				<p>
					Otherwise&nbsp;
					<ActionText disabled={!instrumentBuilder.canExcessToCash()} onClick={() => instrumentBuilder.excessToCash()}>
						Excess to Cash
					</ActionText>
				</p>
			</>
		),
	};
}
